export type Mode = 1 | 2 | 3;
export type UploadedDocument = {
  data: string;
  fileName: string;
  mimeType: string;
};

export const DOCUMENT_MIME_TYPES: Record<string, string> = {
  pdf: "application/pdf",
  doc: "application/msword",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  txt: "text/plain",
};

export function getDocumentMimeType(file: File): string | null {
  const extension = file.name.split(".").pop()?.toLowerCase() ?? "";
  return DOCUMENT_MIME_TYPES[extension] ?? null;
}

export async function readFilesAsBase64(files: File[]): Promise<UploadedDocument[]> {
  return Promise.all(
    files.map((file) => {
      return new Promise<UploadedDocument>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const b64 = (reader.result as string).split(",")[1] ?? "";
          const mimeType = getDocumentMimeType(file);
          if (!b64 || !mimeType) {
            reject(new Error(`無法讀取檔案 ${file.name}`));
            return;
          }
          resolve({
            data: b64,
            fileName: file.name,
            mimeType,
          });
        };
        reader.onerror = () => reject(new Error(`讀取檔案錯誤 ${file.name}`));
        reader.readAsDataURL(file);
      });
    })
  );
}

export async function validateAndReadFiles(
  newFiles: File[],
  existingFiles: File[]
): Promise<{ validFiles: File[]; base64s: UploadedDocument[]; error?: string }> {
  const MAX_FILES = 10;
  const MAX_FILE_SIZE = 20 * 1024 * 1024;
  const MAX_TOTAL_SIZE = 50 * 1024 * 1024;

  const allFilesCount = existingFiles.length + newFiles.length;
  if (allFilesCount > MAX_FILES) {
    return { validFiles: [], base64s: [], error: `最多只可上載 ${MAX_FILES} 個檔案。` };
  }

  let currentTotalSize = existingFiles.reduce((acc, f) => acc + f.size, 0);
  
  const validNewFiles: File[] = [];
  for (const f of newFiles) {
    if (!getDocumentMimeType(f)) {
      return { validFiles: [], base64s: [], error: `檔案「${f.name}」格式不支援。` };
    }
    if (f.size > MAX_FILE_SIZE) {
      return { validFiles: [], base64s: [], error: `檔案「${f.name}」超過單個檔案 20MB 限制。` };
    }
    
    // deduplicate
    if (!existingFiles.some(existing => existing.name === f.name && existing.size === f.size)) {
       validNewFiles.push(f);
       currentTotalSize += f.size;
    }
  }
  
  if (currentTotalSize > MAX_TOTAL_SIZE) {
    return { validFiles: [], base64s: [], error: `總檔案大小超過 50MB 限制。` };
  }

  if (validNewFiles.length === 0) {
    return { validFiles: [], base64s: [] };
  }

  try {
    const b64s = await readFilesAsBase64(validNewFiles);
    return { validFiles: validNewFiles, base64s: b64s };
  } catch (e: any) {
    return { validFiles: [], base64s: [], error: `讀取檔案失敗：${e.message}` };
  }
}
